import { Component, input, signal, computed, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { AppFacade } from '@app/core/services/facades/app.facade';
import { ToastData } from '@core/services/notification.service';

@Component({
    selector: 'app-notification',
    imports: [TranslateModule, ButtonModule],
    template: `
        <div class="toast">
            <p class="toast_title">{{ icon() }} {{ titleKey() | translate }}</p>
            <p class="toast_message">{{ messageKey() | translate: translateParams() }}</p>
            @if (isAdmin && hasError()) {
                <div class="toast_admin">
                    <p-button
                        [text]="true"
                        size="small"
                        severity="secondary"
                        [label]="(isExpanded() ? 'toast.hideError' : 'toast.showError') | translate"
                        [icon]="isExpanded() ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                        iconPos="right"
                        (onClick)="toggleError()"
                    />
                    <div class="expandable-content" data-expandable="true" [style.--expandable-content-height]="expandableContentHeight()">
                        <pre class="toast_error"><code>{{ errorText() }}</code></pre>
                    </div>
                </div>
            }
        </div>
    `,
    styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent {
    private appFacade = inject(AppFacade);
    readonly data = input.required<ToastData>();
    readonly isAdmin = this.appFacade.isAdmin;
    readonly isExpanded = signal(false);

    readonly titleKey = computed(() => {
        const { isMercure, subject } = this.data();
        return isMercure ? `notifs.${subject}` : subject;
    });

    readonly messageKey = computed(() => {
        const { isMercure, message } = this.data();
        return isMercure ? `notifs.${message}` : message || '';
    });

    readonly translateParams = computed(() => ({
        lever: this.data().infos?.lever || '',
        campaign_info: this.data().infos?.campaign_info || '',
        client_name: this.data().infos?.['client_name'] || '',
    }));

    readonly hasError = computed(() => !!this.data().error_value);

    readonly icon = computed(() => {
        const { icon, type } = this.data();
        if (icon) return icon;
        return type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : '';
    });

    readonly expandableContentHeight = computed(() => (this.isExpanded() ? '150px' : '0px'));

    readonly errorText = computed(() => {
        const err = this.data().error_value;
        if (!err) return '';
        if (err instanceof Error) return err.stack || err.message;
        return JSON.stringify(err, null, 2);
    });

    toggleError(): void {
        this.isExpanded.update((v) => !v);
    }
}
